import { existsSync, readFileSync, writeFileSync, mkdirSync, rmSync, statSync } from 'node:fs'
import { resolve, dirname, isAbsolute, relative } from 'node:path'
import { spawn } from 'node:child_process'
import { api, getCachedWorkspace, driftlessHome } from '../api-client'
import { loadCache, saveCache, applySync, needsFullSync, matchCachedTopics, type SyncResponse } from '../cache'
import { recordHookMetric, drainHookMetrics, aggregateMetrics } from '../hook-telemetry'
import { analyticsEvent, flushAnalytics } from '../analytics'
import { currentLocalWorkIntent, saveLocalWorkIntent, type LocalWorkIntent } from '../work-state'

// Driftless ↔ Claude Code hooks. The deterministic counterpart of the skill:
// instead of asking the agent to remember to pull context, a PreToolUse hook
// fires before every edit and injects the team's REVIEWED context for the
// file being touched. Opt-in + experimental.
//
//   driftless hook pre-edit       ← the glue Claude Code calls (reads stdin)
//   driftless hooks enable        ← writes the hook into .claude/settings.json
//   driftless hooks disable       ← removes it

const SETTINGS_PATH = resolve(process.cwd(), '.claude', 'settings.json')
const HOOK_MARKER = 'driftless hook pre-edit'
const POST_MARKER = 'driftless hook post-edit' // PostToolUse — observe what was touched
const STOP_MARKER = 'driftless hook stop' // Stop — nudge to persist what was learned
const MAX_TOPICS = 4 // headers stay scannable; CHAR_BUDGET below is the real cap
// ~2k tokens. Eval-validated budget: a signal-first brief at this size retains
// most invariants/gotchas of the matched topics at ~10% of a raw context dump.
const CHAR_BUDGET = 8000

interface MatchedTopic {
  topic: string
  title?: string | null
  governance?: { authoritative?: boolean }
  invariants?: string[]
  description?: { gotchas?: string[]; decisions?: string | null }
  anchors?: { patterns?: string[]; files?: string[] }
  stale?: { is_stale?: boolean }
}

export async function hookCommand(args: string[]): Promise<void> {
  if (args[0] === 'pre-edit') return preEdit()
  if (args[0] === 'post-edit') return postEdit()
  if (args[0] === 'stop') return stop()
  if (args[0] === 'sync') return syncCacheCmd()
  // Unknown subcommand: stay silent + exit 0 so we never break a tool call.
  process.exit(0)
}

export async function hooksCommand(args: string[]): Promise<void> {
  const sub = args[0]
  if (sub === 'enable') return enable()
  if (sub === 'disable') return disable()
  console.log('Usage: driftless hooks <enable|disable>')
  console.log('  Manages the experimental auto-pull-context hook in .claude/settings.json')
}

// ── pre-edit: the glue ───────────────────────────────────────────────────────
// Reads the PreToolUse payload on stdin, pulls reviewed context for the
// file, dedups per session, and emits it as additionalContext. ALWAYS exits
// 0 and only ever writes the hook JSON to stdout — a failure must never block an
// edit.
async function preEdit(): Promise<void> {
  try {
    const raw = await readStdin()
    const payload = raw ? JSON.parse(raw) : {}
    const filePath = toRepoRelative(payload?.tool_input?.file_path)
    // Claude Code's real session id (when present) — forwarded to the API for
    // delivery traceability; the local dedup files use the 'default' fallback.
    const rawSession: string | undefined =
      typeof payload?.session_id === 'string' && payload.session_id ? payload.session_id : undefined
    const sessionId: string = rawSession || 'default'
    if (!filePath) return done()

    // Active Work overlap warning — independent of the auto-pull toggle (it
    // only ever fires when THIS machine ran `work start`), compact, deduped per
    // session, and fail-open: any error/timeout leaves the edit untouched. It
    // rides the same additionalContext channel as the context brief: whichever
    // exit path fires, the warning (when present) is still emitted.
    const workWarning = await workOverlapWarning(filePath, sessionId)
    const emitAndExit = (context?: string): never => {
      const parts = [workWarning, context].filter(Boolean) as string[]
      if (parts.length > 0) {
        process.stdout.write(
          JSON.stringify({ hookSpecificOutput: { hookEventName: 'PreToolUse', additionalContext: parts.join('\n\n') } }),
        )
      }
      process.exit(0)
    }

    const ws = getCachedWorkspace().slug
    if (!ws) return emitAndExit()

    // Server-side switch: the dashboard toggle (workspace settings.auto_pull_context)
    // decides whether this does anything. The hook stays installed; flipping the
    // toggle off makes it a no-op for the whole workspace. Cached on disk so we
    // don't hit the API on every keystroke.
    if (!(await isAutoPullEnabled(ws))) return emitAndExit()

    // Read path: the local snapshot when one exists (per-Edit cost ~0ms,
    // stale-while-revalidate — a detached `hook sync` refreshes it past TTL),
    // falling back to the API only when no snapshot has been seeded yet.
    const t0 = Date.now()
    let matched: MatchedTopic[]
    const cache = loadCache(ws)
    if (cache) {
      matched = matchCachedTopics(cache, [filePath]) as MatchedTopic[]
      if (Date.now() - cache.fetched_at > CACHE_TTL_MS) spawnCacheRefresh(ws)
    } else {
      spawnCacheRefresh(ws) // seed the snapshot for every edit after this one
      let raw_matched: Array<{ context: MatchedTopic }>
      try {
        // F2.1 — delivery traceability: identify the calling surface ('hook')
        // and the Claude Code session so the server can ledger what context was
        // delivered where. Headers only — the request body stays unchanged.
        raw_matched = (await api.post(`/workspaces/${ws}/topics/match-files`, { files: [filePath] }, {
          timeoutMs: 4000,
          headers: { 'X-Driftless-Source': 'hook', ...(rawSession ? { 'X-Driftless-Session': rawSession } : {}) },
        })) as Array<{ context: MatchedTopic }>
      } catch {
        return emitAndExit() // API down / slow → silently skip, never block the edit
      }
      // match-files returns [{ context, history, match_reason }] — unwrap context.
      matched = (raw_matched || []).map((m) => m?.context).filter(Boolean)
    }

    const seen = loadSeen(sessionId)
    // Trust filter: only REVIEWED context (the team's vouched truth), and only
    // what we haven't already injected this session (dedup — editing the same
    // area 5× injects once, not five times).
    const relevant = (matched || []).filter(
      (t) => t.governance?.authoritative && !seen.has(t.topic),
    )
    if (relevant.length === 0) {
      recordHookMetric(ws, { at: Date.now(), ms: Date.now() - t0, tokens: 0, cache_hit: !!cache, matched: matched?.length ?? 0, injected: false })
      return emitAndExit()
    }

    const picked = relevant
      .map((t) => ({ t, score: topicScore(t, filePath) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_TOPICS)
      .map((x) => x.t)
    const brief = buildBrief(picked)
    if (!brief) return emitAndExit()

    picked.forEach((t) => seen.add(t.topic))
    saveSeen(sessionId, seen)
    // Buffered locally (microseconds); the detached sync child ships it later.
    recordHookMetric(ws, { at: Date.now(), ms: Date.now() - t0, tokens: Math.ceil(brief.length / 4), cache_hit: !!cache, matched: matched.length, injected: true })

    return emitAndExit(brief)
  } catch {
    // swallow everything — the edit must proceed regardless
  }
  process.exit(0)
}

// ── post-edit: observe what the agent touched (PostToolUse) ──────────────────
// Records the edited file into the session's touched-set so the Stop nudge can
// suggest a precise anchor. Local-only (no network) — never slows an edit.
async function postEdit(): Promise<void> {
  try {
    const raw = await readStdin()
    const payload = raw ? JSON.parse(raw) : {}
    const filePath = toRepoRelative(payload?.tool_input?.file_path)
    const sessionId: string = payload?.session_id || 'default'
    if (filePath) {
      appendTouched(sessionId, filePath)
      // Active Work write-back: stream the touched file into the live intent's
      // observed_files (which also renews the heartbeat). Bounded, no model
      // call, and fail-open — a dead API never slows or blocks the tool call.
      await observeWorkFile(filePath)
    }
  } catch { /* never block a tool call */ }
  process.exit(0)
}

// ── Active Work glue (fail-open by contract) ─────────────────────────────────
// All three hooks below only ever do anything when THIS machine has a live
// `work start` session recorded for the current repo/branch. Every network call
// carries a short timeout and zero retries; every failure path is silence.

const WORK_HTTP = { timeoutMs: 2500, retries: 0 } as const
const WORK_HEARTBEAT_MIN_MS = 60_000

// PostToolUse: report a touched file once (dedup via local state); repeat edits
// inside the heartbeat window are a no-op, past it a bare heartbeat renews TTL.
export async function observeWorkFile(filePath: string): Promise<void> {
  try {
    const local = currentLocalWorkIntent()
    if (!local) return
    const reported = new Set(local.reported_files ?? [])
    const lastSync = local.last_synced_at ? Date.parse(local.last_synced_at) : 0
    if (reported.has(filePath)) {
      if (Date.now() - lastSync < WORK_HEARTBEAT_MIN_MS) return
      await api.post(`/workspaces/${local.workspace}/work-intents/${encodeURIComponent(local.intent_id)}/heartbeat`, {}, WORK_HTTP)
    } else {
      await api.patch(
        `/workspaces/${local.workspace}/work-intents/${encodeURIComponent(local.intent_id)}`,
        { observed_files: [filePath] },
        WORK_HTTP,
      )
      reported.add(filePath)
    }
    saveLocalWorkIntent({
      ...local,
      reported_files: [...reported].slice(-200),
      last_synced_at: new Date().toISOString(),
    })
  } catch {
    /* fail-open: the observation is best-effort, the edit already happened */
  }
}

// PreToolUse: a COMPACT one-shot warning when another live session declared or
// touched the exact file being edited. Exact-match only on purpose — the
// definitive overlap semantics (narrow-parent, topics, areas) stay server-side
// in work.start's preflight; this is a display-level heads-up. Deduped per
// session+intent+file. The active list is cached ~30s so edits don't pay a
// round-trip each keystroke.
const WORK_ACTIVE_CACHE_MS = 30_000
interface ActiveWorkRow { id: string; objective?: string; agent_name?: string | null; actor_id?: string | null; declared_files?: string[]; observed_files_count?: number }

function workActiveCachePath(): string {
  return resolve(driftlessHome(), 'work-active-cache.json')
}
function workWarnedPath(sessionId: string): string {
  return resolve(driftlessHome(), `work-warned-${sanitize(sessionId)}.json`)
}

async function fetchActiveWork(local: LocalWorkIntent): Promise<ActiveWorkRow[]> {
  try {
    const p = workActiveCachePath()
    if (existsSync(p)) {
      const c = JSON.parse(readFileSync(p, 'utf8')) as { at: number; key: string; rows: ActiveWorkRow[] }
      if (c && c.key === `${local.repository_key}::${local.branch}` && Date.now() - c.at < WORK_ACTIVE_CACHE_MS) return c.rows
    }
  } catch { /* stale/corrupt cache → refetch */ }
  const res = (await api.get(
    `/workspaces/${local.workspace}/work-intents/active?repository_key=${encodeURIComponent(local.repository_key)}&branch=${encodeURIComponent(local.branch)}`,
    WORK_HTTP,
  )) as { intents?: ActiveWorkRow[] }
  const rows = res?.intents ?? []
  try {
    mkdirSync(dirname(workActiveCachePath()), { recursive: true })
    writeFileSync(workActiveCachePath(), JSON.stringify({ at: Date.now(), key: `${local.repository_key}::${local.branch}`, rows }))
  } catch { /* best effort */ }
  return rows
}

export async function workOverlapWarning(filePath: string, sessionId: string): Promise<string | null> {
  try {
    const local = currentLocalWorkIntent()
    if (!local) return null
    const warned = new Set<string>((() => {
      try { return JSON.parse(readFileSync(workWarnedPath(sessionId), 'utf8')) as string[] } catch { return [] }
    })())
    const others = (await fetchActiveWork(local)).filter((w) => w.id !== local.intent_id)
    const hit = others.find((w) => (w.declared_files ?? []).includes(filePath))
    if (!hit) return null
    const key = `${hit.id}:${filePath}`
    if (warned.has(key)) return null
    warned.add(key)
    try {
      mkdirSync(dirname(workWarnedPath(sessionId)), { recursive: true })
      writeFileSync(workWarnedPath(sessionId), JSON.stringify([...warned].slice(-200)))
    } catch { /* best effort */ }
    const who = hit.agent_name || hit.actor_id || 'another agent'
    return (
      `Driftless Active Work: another live session (${who} — "${hit.objective ?? ''}") declared \`${filePath}\` in its plan. ` +
      `You are not blocked, but coordinate: check \`driftless work active\` before making conflicting changes.`
    )
  } catch {
    return null // fail-open: warning is optional, the edit never waits on it
  }
}

// Stop: one compact nudge per session when a live intent was never closed. It
// never closes the intent and never touches the Card — informational only
// (systemMessage, not a block).
function workStopNudgedPath(sessionId: string): string {
  return resolve(driftlessHome(), `work-stop-nudged-${sanitize(sessionId)}.json`)
}
export function workStopNudge(sessionId: string): string | null {
  try {
    const local = currentLocalWorkIntent()
    if (!local) return null
    if (existsSync(workStopNudgedPath(sessionId))) return null
    try {
      mkdirSync(dirname(workStopNudgedPath(sessionId)), { recursive: true })
      writeFileSync(workStopNudgedPath(sessionId), '1')
    } catch { /* best effort */ }
    return (
      `Driftless: this session's work intent ("${local.objective}") is still active. ` +
      `Close it so other agents stop seeing you as live: driftless work finish --outcome success ` +
      `[--card-status review|done|blocked|in_progress] — or driftless work abandon.`
    )
  } catch {
    return null
  }
}

// Claude Code sends ABSOLUTE file paths in tool_input.file_path; topic anchors
// are repo-relative globs. Relativize against cwd (Claude Code runs hook
// commands at the project root) or no anchor can ever match — found by the
// agent-in-the-loop eval, invisible to every hand-fed test that passed
// relative paths. Paths outside the project stay unmatched on purpose.
export function toRepoRelative(p: unknown): string | undefined {
  if (typeof p !== 'string' || p.length === 0) return undefined
  if (!isAbsolute(p)) return p
  const rel = relative(process.cwd(), p)
  if (rel.startsWith('..')) return p // outside the project — won't (and shouldn't) match
  return rel
}

// ── stop: nudge the agent to persist what it learned (Stop) ──────────────────
// Write-after, symmetric to pre-edit's read-before. When the session touched a
// SOURCE boundary that NO topic covers (a real gap), nudge ONCE to persist the
// durable context — with a suggested anchor derived from the files actually
// touched. Gated by the workspace toggle, one-time per session, silent otherwise.
async function stop(): Promise<void> {
  try {
    const raw = await readStdin()
    const payload = raw ? JSON.parse(raw) : {}
    const sessionId: string = payload?.session_id || 'default'
    // Never loop: if this stop is itself a hook continuation, stay silent.
    if (payload?.stop_hook_active) return done()

    // Active Work: an unclosed live intent gets ONE compact, non-blocking nudge
    // (systemMessage) to run `work finish` / `work abandon`. Never auto-closes,
    // never touches the Card. Emitted at whichever exit path fires.
    const workNudge = workStopNudge(sessionId)
    const exitWithNudge = (): never => {
      if (workNudge) process.stdout.write(JSON.stringify({ systemMessage: workNudge }))
      process.exit(0)
    }

    const touched = loadTouched(sessionId).filter(isSourceFile)
    if (touched.length === 0) return exitWithNudge()

    const ws = getCachedWorkspace().slug
    if (!ws || !(await isAutoPullEnabled(ws))) { clearTouched(sessionId); return exitWithNudge() }
    if (alreadyNudged(sessionId)) { clearTouched(sessionId); return exitWithNudge() }

    // Only nudge on a real GAP: the touched area has NO covering topic at all.
    // If something matches, the agent already had (or could pull) context — don't
    // nag. Served from the local snapshot when present, same as pre-edit.
    const cache = loadCache(ws)
    let matchedCount: number
    if (cache) {
      matchedCount = matchCachedTopics(cache, touched.slice(0, 20)).length
    } else {
      try {
        const matched = (await api.post(`/workspaces/${ws}/topics/match-files`, { files: touched.slice(0, 20) }, { timeoutMs: 4000 })) as unknown[]
        matchedCount = Array.isArray(matched) ? matched.length : 0
      } catch {
        return done() // can't tell → never nudge
      }
    }
    if (matchedCount > 0) { clearTouched(sessionId); return exitWithNudge() }

    const boundary = commonBoundary(touched)
    markNudged(sessionId)
    clearTouched(sessionId)
    const reason =
      `Driftless: you edited ${touched.length} source file(s) under \`${boundary}\` — an area with no recorded team context. ` +
      `If you learned something durable here (a decision, an invariant, a gotcha that outlives the line), persist it so the next agent inherits it:\n` +
      `  driftless context add <slug> --title "..." --what "..." --pattern "${boundary}/**" --decision "..."\n` +
      `Skip if nothing durable came up — this is a one-time, opt-in nudge.` +
      (workNudge ? `\n\n${workNudge}` : '')
    process.stdout.write(JSON.stringify({ decision: 'block', reason }))
  } catch {
    /* swallow — never break the session */
  }
  process.exit(0)
}

// Is the auto-pull toggle ON for this workspace? Cached on disk (5 min, also
// force-refreshed by every `hook sync`) so steady-state edits make no network
// call at all. The trade-off is explicit: flipping the dashboard toggle takes
// effect on a machine within minutes, not instantly — acceptable for an
// opt-in convenience switch, and the price of a zero-latency edit path.
const FLAG_TTL_MS = 5 * 60_000
async function isAutoPullEnabled(ws: string, forceRefresh = false): Promise<boolean> {
  const cachePath = resolve(driftlessHome(), `hook-flag-${ws.replace(/[^a-zA-Z0-9_-]/g, '')}.json`)
  try {
    if (!forceRefresh && existsSync(cachePath)) {
      const c = JSON.parse(readFileSync(cachePath, 'utf8')) as { at: number; on: boolean }
      if (Date.now() - c.at < FLAG_TTL_MS) return c.on
    }
  } catch { /* ignore */ }
  let on = false
  try {
    const wsData = (await api.get(`/workspaces/${ws}`, { timeoutMs: 3000 })) as { settings?: Record<string, unknown> }
    on = wsData?.settings?.['auto_pull_context'] === true
  } catch {
    return false // can't reach the API → safest is off
  }
  try {
    mkdirSync(dirname(cachePath), { recursive: true })
    writeFileSync(cachePath, JSON.stringify({ at: Date.now(), on }))
  } catch { /* best effort */ }
  return on
}

// ── local snapshot sync (the delta-sync half of the read path) ───────────────
// Snapshot freshness window. Within it edits are fully offline; past it the
// NEXT edit still serves from the (slightly stale) snapshot and a detached
// child refreshes in the background — stale-while-revalidate, the edit never
// waits. Worst case a just-approved topic reaches a machine one TTL late.
const CACHE_TTL_MS = 5 * 60_000

// Anti-stampede: many edits can land in the same TTL-expired window before the
// first refresh writes the new snapshot. One marker file rate-limits spawns.
function refreshMarkerPath(ws: string): string {
  return resolve(driftlessHome(), 'cache', `${ws.replace(/[^a-zA-Z0-9_-]/g, '')}.refreshing`)
}

function spawnCacheRefresh(ws: string): void {
  try {
    const marker = refreshMarkerPath(ws)
    try {
      if (existsSync(marker) && Date.now() - statSync(marker).mtimeMs < 30_000) return
    } catch { /* ignore */ }
    mkdirSync(dirname(marker), { recursive: true })
    writeFileSync(marker, '1')
    // process.argv[1] is the CLI entry itself — re-invoke it detached so the
    // edit returns immediately and the sync finishes on its own.
    const child = spawn(process.execPath, [process.argv[1], 'hook', 'sync'], { detached: true, stdio: 'ignore' })
    child.unref()
  } catch { /* best effort — next edit retries */ }
}

async function syncCacheCmd(): Promise<void> {
  try {
    const ws = getCachedWorkspace().slug
    if (!ws) process.exit(0)
    await isAutoPullEnabled(ws, true) // refresh the toggle cache in the same pass
    const prev = loadCache(ws)
    const qs = prev?.cursor ? `?since=${encodeURIComponent(prev.cursor)}` : ''
    const resp = (await api.get(`/workspaces/${ws}/topics/sync${qs}`, { timeoutMs: 20_000 })) as SyncResponse
    let next = applySync(prev, resp, Date.now())
    // Self-heal: a manifest topic with no body (visibility changed without a
    // last_updated bump, corrupt file) or an outdated body (raced write) →
    // one full snapshot closes every gap.
    if (needsFullSync(next, resp)) {
      const full = (await api.get(`/workspaces/${ws}/topics/sync`, { timeoutMs: 30_000 })) as SyncResponse
      next = applySync(null, full, Date.now())
    }
    saveCache(ws, next)
    try { rmSync(refreshMarkerPath(ws)) } catch { /* ignore */ }
    // Ship buffered hook metrics — here in the background child, where waiting
    // costs nothing, never on the edit path. One aggregate event per batch.
    const agg = aggregateMetrics(drainHookMetrics(ws))
    if (agg) {
      void analyticsEvent('cli_hook_inject', ws, agg)
      await flushAnalytics(1500)
    }
  } catch {
    /* silent — the hook path falls back to the API while no snapshot exists */
  }
  process.exit(0)
}

// Order topics by how specifically they anchor the edited file. Measured on
// hand-labeled cases: arbitrary order puts the right topic first ~38% of the
// time, specificity+freshness ~56% — better but imperfect, so the score decides
// ORDER (whose signal enters the budget first); it never drops a topic.
//
// Specificity without a glob lib (the CLI ships dependency-free): a pattern's
// literal prefix — the segments before its first wildcard — is checked against
// the file, and the depth of the deepest covering anchor wins. where_files are
// segment-prefix anchors by definition. Stale context ranks below fresh.
export function topicScore(t: MatchedTopic, file: string): number {
  let depth = 0
  for (const p of t.anchors?.patterns ?? []) {
    const segs = p.split('/')
    const firstWild = segs.findIndex((s) => /[*?]/.test(s))
    const lit = (firstWild === -1 ? segs : segs.slice(0, firstWild)).join('/')
    if (lit === '' || file === lit || file.startsWith(lit + '/')) depth = Math.max(depth, segs.length)
  }
  for (const wf of t.anchors?.files ?? []) {
    const a = wf.replace(/\/+$/, '')
    if (file === a || file.startsWith(a + '/')) depth = Math.max(depth, a.split('/').length)
  }
  return (depth || 1) * (t.stale?.is_stale ? 0.7 : 1)
}

// Signal-first brief, ONE block for all matched topics: headers, then EVERY
// topic's invariants+gotchas before ANY topic's decisions. Eval-validated:
// global signal-first retains more invariants/gotchas under the same budget
// than splitting the budget per topic. Signal items are drawn ROUND-ROBIN
// across topics (score order within each round) so one gotcha-heavy topic
// can't starve the others out of the budget. Whole-item only — an invariant
// that doesn't fit is skipped, never truncated mid-sentence.
export function buildBrief(picked: MatchedTopic[]): string {
  const lines: string[] = []
  let used = 0
  const tryPush = (line: string): boolean => {
    if (used + line.length + 1 > CHAR_BUDGET) return false
    lines.push(line)
    used += line.length + 1
    return true
  }
  for (const t of picked) {
    tryPush(`▌ ${t.title || t.topic} — ✓ Reviewed${t.stale?.is_stale ? ' (STALE — code moved since this was written; verify)' : ''}`)
  }
  const tag = (t: MatchedTopic) => (picked.length > 1 ? `[${t.topic}] ` : '')
  const queues = picked.map((t) => [
    ...(t.invariants ?? []).map((x) => `  ${tag(t)}invariant: ${x}`),
    ...(t.description?.gotchas ?? []).map((x) => `  ${tag(t)}gotcha: ${x}`),
  ])
  while (queues.some((q) => q.length > 0)) {
    for (const q of queues) {
      const item = q.shift()
      if (item) tryPush(item)
    }
  }
  for (const t of picked) {
    if (t.description?.decisions) tryPush(`  ${tag(t)}decisions: ${oneLine(t.description.decisions)}`)
  }
  return lines.join('\n')
}

function oneLine(s: string): string {
  return s.replace(/\s+/g, ' ').trim().slice(0, 300)
}

function done(): never {
  process.exit(0)
}

// ── session dedup ────────────────────────────────────────────────────────────
function seenPath(sessionId: string): string {
  return resolve(driftlessHome(), `hook-seen-${sessionId.replace(/[^a-zA-Z0-9_-]/g, '')}.json`)
}
function loadSeen(sessionId: string): Set<string> {
  try {
    const p = seenPath(sessionId)
    if (existsSync(p)) return new Set(JSON.parse(readFileSync(p, 'utf8')) as string[])
  } catch { /* ignore */ }
  return new Set()
}
function saveSeen(sessionId: string, seen: Set<string>): void {
  try {
    const p = seenPath(sessionId)
    mkdirSync(dirname(p), { recursive: true })
    writeFileSync(p, JSON.stringify([...seen]))
  } catch { /* best effort */ }
}

// ── session touched-set + one-time nudge flag (write-side) ───────────────────
const sanitize = (s: string): string => s.replace(/[^a-zA-Z0-9_-]/g, '')
const touchedPath = (sessionId: string): string => resolve(driftlessHome(), `hook-touched-${sanitize(sessionId)}.json`)
const nudgedPath = (sessionId: string): string => resolve(driftlessHome(), `hook-nudged-${sanitize(sessionId)}.json`)

function appendTouched(sessionId: string, file: string): void {
  try {
    const set = new Set(loadTouched(sessionId))
    set.add(file)
    const p = touchedPath(sessionId)
    mkdirSync(dirname(p), { recursive: true })
    writeFileSync(p, JSON.stringify([...set].slice(-200))) // cap the set
  } catch { /* best effort */ }
}
function loadTouched(sessionId: string): string[] {
  try {
    const p = touchedPath(sessionId)
    if (existsSync(p)) return JSON.parse(readFileSync(p, 'utf8')) as string[]
  } catch { /* ignore */ }
  return []
}
function clearTouched(sessionId: string): void {
  try { const p = touchedPath(sessionId); if (existsSync(p)) rmSync(p) } catch { /* ignore */ }
}
function alreadyNudged(sessionId: string): boolean { return existsSync(nudgedPath(sessionId)) }
function markNudged(sessionId: string): void {
  try { const p = nudgedPath(sessionId); mkdirSync(dirname(p), { recursive: true }); writeFileSync(p, '1') } catch { /* ignore */ }
}

const SOURCE_RE = /\.(ts|tsx|js|jsx|mjs|cjs)$/
function isSourceFile(f: string): boolean {
  if (!SOURCE_RE.test(f)) return false
  if (/(^|\/)(node_modules|dist|build|\.next|coverage)(\/|$)/.test(f)) return false
  if (/\.(spec|test|d)\.[mc]?[tj]sx?$/.test(f)) return false
  return true
}
// The narrowest directory containing every touched file — the boundary to anchor
// to (the suggested glob in the nudge). The agent narrows it; we just hand it the
// signal from what was actually edited (no outsmarting — it decides).
function commonBoundary(files: string[]): string {
  const dirs = files.map((f) => f.split('/').slice(0, -1))
  if (dirs.length === 0) return 'src'
  let prefix = dirs[0]
  for (const d of dirs.slice(1)) {
    let i = 0
    while (i < prefix.length && i < d.length && prefix[i] === d[i]) i++
    prefix = prefix.slice(0, i)
  }
  return prefix.length ? prefix.join('/') : (dirs[0][0] ?? 'src')
}

// ── enable / disable (manage .claude/settings.json) ──────────────────────────
function readSettings(): any {
  try {
    if (existsSync(SETTINGS_PATH)) return JSON.parse(readFileSync(SETTINGS_PATH, 'utf8'))
  } catch { /* fall through to {} */ }
  return {}
}
function writeSettings(s: any): void {
  mkdirSync(dirname(SETTINGS_PATH), { recursive: true })
  writeFileSync(SETTINGS_PATH, JSON.stringify(s, null, 2) + '\n')
}

// Writes the PreToolUse hook into .claude/settings.json (idempotent). Silent —
// returns true if it added it, false if it was already there. Reused by
// install-skill so the hook ships by default; the dashboard toggle gates it.
export function installPreEditHook(): boolean {
  const s = readSettings()
  s.hooks = s.hooks || {}
  const list: any[] = s.hooks.PreToolUse || (s.hooks.PreToolUse = [])
  if (JSON.stringify(list).includes(HOOK_MARKER)) return false
  list.push({
    matcher: 'Edit|Write|MultiEdit',
    hooks: [{ type: 'command', command: HOOK_MARKER }],
  })
  writeSettings(s)
  return true
}

// PostToolUse — observe what the agent touches (write-side signal). Ships with
// install-skill; gated by the same workspace toggle as the read hook.
export function installPostEditHook(): boolean {
  const s = readSettings()
  s.hooks = s.hooks || {}
  const list: any[] = s.hooks.PostToolUse || (s.hooks.PostToolUse = [])
  if (JSON.stringify(list).includes(POST_MARKER)) return false
  list.push({
    matcher: 'Edit|Write|MultiEdit',
    hooks: [{ type: 'command', command: POST_MARKER }],
  })
  writeSettings(s)
  return true
}

// Stop — the reflection nudge at session end (write-after).
export function installStopHook(): boolean {
  const s = readSettings()
  s.hooks = s.hooks || {}
  const list: any[] = s.hooks.Stop || (s.hooks.Stop = [])
  if (JSON.stringify(list).includes(STOP_MARKER)) return false
  list.push({ hooks: [{ type: 'command', command: STOP_MARKER }] })
  writeSettings(s)
  return true
}

const HOOK_EVENTS: ReadonlyArray<readonly [string, string]> = [
  ['PreToolUse', HOOK_MARKER],
  ['PostToolUse', POST_MARKER],
  ['Stop', STOP_MARKER],
]

function enable(): void {
  const added = [installPreEditHook(), installPostEditHook(), installStopHook()].some(Boolean)
  console.log(added
    ? '✓ Installed the Driftless context hooks (read-before + write-after) in .claude/settings.json'
    : '✓ Driftless context hooks already installed in .claude/settings.json')
  console.log('  Gated by the workspace toggle — turn it ON at Settings → Automations to activate.')
  console.log('  Remove them entirely with: driftless hooks disable')
}

function disable(): void {
  const s = readSettings()
  let changed = false
  for (const [event, marker] of HOOK_EVENTS) {
    const list: any[] = s?.hooks?.[event]
    if (Array.isArray(list)) {
      s.hooks[event] = list.filter((entry) => !JSON.stringify(entry).includes(marker))
      changed = true
    }
  }
  if (!changed) {
    console.log('Driftless context hooks are not enabled.')
    return
  }
  writeSettings(s)
  console.log('✓ Disabled the Driftless context hooks.')
}

function readStdin(): Promise<string> {
  return new Promise((resolveP) => {
    if (process.stdin.isTTY) return resolveP('')
    let data = ''
    process.stdin.setEncoding('utf8')
    process.stdin.on('data', (c) => (data += c))
    process.stdin.on('end', () => resolveP(data))
    setTimeout(() => resolveP(data), 2500) // safety: never hang the editor
  })
}
